// Application Components
import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { object, string } from 'yup'
import { translateField } from '../../functions/field-functions'
import { extractSubmissionForm } from '../../functions/form-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateFormData,
    updateFormValidity,
    updateFormSystem,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import DividerComponent from '../../components/simple-components/divider-component'
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import FileUploadComponent from '../../components/file-upload-components/file-upload-component'

import NotificationComponent from '../../components/conditional-components/notification-component'

//// Carbon Components
import { Form, RadioButtonGroup, RadioButton } from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const HealthHistoryForm = ({ instance }) => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const submit_route = settingsData?.['form']?.['submit_route']
    const fields = settingsData?.['form']?.['fields']

    //////// Search Params

    //////// Methods

    // Validate Data Submission
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            schema.yup = object({
                IMP_ABILITY: string().required(
                    'This field is required to be entered or selected.'
                ),
                IPP: string().required(
                    'This field is required to be entered or selected.'
                ),
                MDNI: string().required(
                    'This field is required to be entered or selected.'
                ),
                RSD: string().required(
                    'This field is required to be entered or selected.'
                ),
                TSRD: string().required(
                    'This field is required to be entered or selected.'
                ),
                IPN_QUEST: string(),
            })
            schema.valid = {
                IMP_ABILITY: false,
                IMP_ABILITYErrMsg: '',
                IPP: false,
                IPPErrMsg: '',
                MDNI: false,
                MDNIErrMsg: '',
                RSD: false,
                RSDErrMsg: '',
                TSRD: false,
                TSRDErrMsg: '',
                IPN_QUEST: false,
                IPN_QUESTErrMsg: '',
            }
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateField(fields, el?.path)
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    // Re-Validate Data Submission
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateFormValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    //////// Functional Methods

    // Handle Data Of All Fields
    const handleData = (e) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        }
        //// Start Watson Assistant Journey

        if (e?.target?.name === 'IPP' && e?.target?.value === 'Y') {
            instance.tours.startTour(
                'Registered Nursing Endorsement Health History Yes Tour'
            )
        } else if (e?.target?.name === 'IPP' && e?.target?.value === 'N') {
            instance.tours.startTour(
                'Registered Nursing Endorsement Health History Disorder Tour'
            )
        } else if (e?.target?.name === 'TSRD' && e?.target?.value === 'Y') {
            instance.tours.startTour(
                'Registered Nursing Endorsement Health History Yes Tour'
            )
        } else if (e?.target?.name === 'TSRD' && e?.target?.value === 'N') {
            instance.tours.startTour(
                'Registered Nursing Endorsement Health History Substance Tour'
            )
        } else if (
            e?.target?.name === 'IPN_QUEST' &&
            e?.target?.value === 'Y'
        ) {
            instance.tours.startTour(
                'Registered Nursing Endorsement Health History IPN Tour'
            )
        } else if (
            (e?.target?.name === 'IMP_ABILITY' && e?.target?.value === 'Y') ||
            (e?.target?.name === 'MDNI' && e?.target?.value === 'Y') ||
            (e?.target?.name === 'RSD' && e?.target?.value === 'Y')
        ) {
            instance.tours.startTour(
                'Registered Nursing Endorsement Health History Yes Tour'
            )
        } else {
            instance.tours.endTour()
        }

        //// End Watson Assistant Journey
    }

    // Handle Form Submission
    const handleSubmit = async () => {
        try {
            console.info('Health History: Submitted')

            setLoading(true)

            // Extract Form Data From Redux
            const submit = await extractSubmissionForm(digital_name, formsData)

            // Validate Form Submission Data
            const valid = await validation(submit)

            // Check If Form Data Is Valid
            if (valid) {
                console.info('Health History: Passed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(submit_route ? submit_route : '/')
            } else {
                console.info('Health History: Failed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: false },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Form Errors:',
                    subtitle:
                        'There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.',
                })

                console.log(
                    'Health History Failed Valid: There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Down Key Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects
    // Reload Watson Assistant Journey on Reload
    useEffect(() => {
        if (instance?.tours) {
            instance.tours.endTour()
            instance.tours.startTour(
                'Registered Nursing Endorsement Health History Tour'
            )
        }
        return () => {}
    }, [instance])

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <LoadingComponent />
                </Form>
            </>
        )
    } else {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <p>
                        <strong>
                            This information is exempt from public records
                            disclosure.
                        </strong>
                    </p>

                    <p>
                        <strong>
                            If you fail to disclose the information requested in
                            this section, your application may be denied.
                        </strong>
                    </p>

                    <DividerComponent text="Physical and Mental Health Disorders Impacting Ability to Practice" />

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.IMP_ABILITY
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.IMP_ABILITY
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.IMP_ABILITY
                        }
                        legendText="A. During the last two years, have you been treated for or had a recurrence of a diagnosed physical or mental disorder that impaired or would impair your ability to practice?"
                        name="IMP_ABILITY"
                        invalid={formsData?.[digital_name]?.errors?.IMP_ABILITY}
                        invalidText={
                            formsData?.[digital_name]?.errors?.IMP_ABILITYErrMsg
                        }
                    >
                        <RadioButton
                            id="IMP_ABILITYYes"
                            name="IMP_ABILITYYes"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={handleData}
                        />
                        <RadioButton
                            id="IMP_ABILITYNo"
                            name="IMP_ABILITYNo"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={handleData}
                        />
                    </RadioButtonGroup>

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.IPP
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={formsData?.[digital_name]?.values?.IPP}
                        valueSelected={formsData?.[digital_name]?.values?.IPP}
                        legendText="B. In the last two years, have you been admitted or referred to a hospital, facility or impaired practitioner program for treatment of a diagnosed mental or physical disorder that impaired your ability to practice?"
                        name="IPP"
                        invalid={formsData?.[digital_name]?.errors?.IPP}
                        invalidText={
                            formsData?.[digital_name]?.errors?.IPPErrMsg
                        }
                    >
                        <RadioButton
                            id="IPPYes"
                            name="IPPYes"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={handleData}
                        />
                        <RadioButton
                            id="IPPNo"
                            name="IPPNo"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={handleData}
                        />
                    </RadioButtonGroup>

                    <DividerComponent text="Substance-Related Disorders Impacting Ability to Practice" />

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.MDNI
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.MDNI
                        }
                        valueSelected={formsData?.[digital_name]?.values?.MDNI}
                        legendText="C. During the last five years, have you been treated for or had a recurrence of a diagnosed substance-related (alcohol or drug) disorder that impaired or would impair your ability to practice?"
                        name="MDNI"
                        invalid={formsData?.[digital_name]?.errors?.MDNI}
                        invalidText={
                            formsData?.[digital_name]?.errors?.MDNIErrMsg
                        }
                    >
                        <RadioButton
                            id="MDNIYes"
                            name="MDNIYes"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={handleData}
                        />
                        <RadioButton
                            id="MDNINo"
                            name="MDNINo"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={handleData}
                        />
                    </RadioButtonGroup>

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.RSD
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={formsData?.[digital_name]?.values?.RSD}
                        valueSelected={formsData?.[digital_name]?.values?.RSD}
                        legendText="D. During the last five years, were you admitted or directed into a program for the treatment of a diagnosed substance-related (alcohol or drug) disorder or, if you were previously in such a program, did you suffer a relapse?"
                        name="RSD"
                        invalid={formsData?.[digital_name]?.errors?.RSD}
                        invalidText={
                            formsData?.[digital_name]?.errors?.RSDErrMsg
                        }
                    >
                        <RadioButton
                            id="RSDYes"
                            name="RSDYes"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={handleData}
                        />
                        <RadioButton
                            id="RSDNo"
                            name="RSDNo"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={handleData}
                        />
                    </RadioButtonGroup>

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.TSRD
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.TSRD
                        }
                        valueSelected={formsData?.[digital_name]?.values?.TSRD}
                        legendText="E. During the last five years, have you been enrolled in, required to enter, or participated in any substance related (alcohol or drug) recovery program or impaired practitioner program for treatment of drug or alcohol abuse?"
                        name="TSRD"
                        invalid={formsData?.[digital_name]?.errors?.TSRD}
                        invalidText={
                            formsData?.[digital_name]?.errors?.TSRDErrMsg
                        }
                    >
                        <RadioButton
                            id="TSRDYes"
                            name="TSRDYes"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={handleData}
                        />
                        <RadioButton
                            id="TSRDNo"
                            name="TSRDNo"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={handleData}
                        />
                    </RadioButtonGroup>

                    <p>
                        <strong>
                            If a {`"Yes"`} response was provided to any of the
                            questions in this section
                        </strong>
                        , provide the following documents directly to the board
                        office:
                    </p>

                    <p>
                        <strong>
                            A letter from a Licensed Health Care Practitioner
                        </strong>
                        , who is qualified by skill and training to address the
                        condition identified, which explains the impact the
                        condition may have on the ability to practice the
                        profession with reasonable skill and safety. The letter
                        must specify that the applicant is safe to practice the
                        profession without restrictions or specifically indicate
                        the restrictions that are necessary.
                    </p>

                    <p>
                        Documentation provided must be dated within one year of
                        the application date.
                    </p>

                    <p>
                        <strong>A written self-explanation</strong>, identifying
                        the medical condition(s) or occurrence(s), and current
                        status.
                    </p>

                    <DividerComponent text="For Multistate Applicants ONLY" />

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.IPN_QUEST
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.IPN_QUEST
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.IPN_QUEST
                        }
                        legendText="Are you a current participant in an alternative to discipline program [i.e. Intervention Project for Nurses (IPN)]?"
                        name="IPN_QUEST"
                        invalid={formsData?.[digital_name]?.errors?.IPN_QUEST}
                        invalidText={
                            formsData?.[digital_name]?.errors?.IPN_QUESTErrMsg
                        }
                    >
                        <RadioButton
                            id="IPN_QUESTYes"
                            name="IPN_QUESTYes"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={handleData}
                        />
                        <RadioButton
                            id="IPN_QUESTNo"
                            name="IPN_QUESTNo"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={handleData}
                        />
                    </RadioButtonGroup>
                </Form>

                <div className="file-upload-group">
                    <div className="file-upload-group-item">
                        <FileUploadComponent />
                    </div>
                </div>

                <NotificationComponent notification={notification} />

                <div className="button-group">
                    <div className="button-group-item">
                        <ButtonComponent
                            click={handleSubmit}
                            title="Save Progress"
                        />
                    </div>
                </div>
            </>
        )
    }
}

export default HealthHistoryForm
